import type { InferContractRouterOutputs } from '@orpc/contract'
import { useInfiniteQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash-es'
import type { baseContract } from 'pixai-contract/api/orpc'
import { useMemo } from 'react'
import type { ArtworkPreviewFragment } from 'src/services/apollo/generated/graphql'
import { orpc } from './contract'
import { toArtworkPreviewFragment } from './utils/typeAdapters'

type DailyRankingArtworksPage = InferContractRouterOutputs<
  typeof baseContract
>['artwork']['getDailyRankingArtworks']

const getNextPageParam = (lastPage: DailyRankingArtworksPage) => {
  if (!lastPage.pageInfo.hasNextPage) return undefined
  return lastPage.pageInfo.endCursor
}

interface DailyRankingArtworksResult {
  artworks: ArtworkPreviewFragment[]
  pageInfo: {
    hasNextPage: boolean
    endCursor?: string
  }
  loading: boolean
  loadingMore: boolean
  fetchMore: () => void
}

export function useOrpcDailyRankingArtworks(
  args: { first: number } | null,
): DailyRankingArtworksResult {
  const enabled = args != null
  const first = args?.first

  const result = useInfiniteQuery({
    ...orpc.artwork.getDailyRankingArtworks.infiniteOptions({
      input: (pageParam: string | undefined) => ({
        first,
        after: pageParam,
      }),
      getNextPageParam,
      initialPageParam: undefined as string | undefined,
    }),
    enabled,
    // `liked` depends on the current user — drop cache across login/logout.
    meta: { dependsOn: ['login'] },
  })

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    result

  const artworks = useMemo(
    () =>
      uniqBy(
        data?.pages.flatMap(page =>
          page.data.map(item => toArtworkPreviewFragment(item)),
        ) ?? [],
        'id',
      ),
    [data?.pages],
  )
  const lastPage = data?.pages.at(-1)

  return {
    artworks,
    pageInfo: {
      hasNextPage: hasNextPage ?? false,
      endCursor: lastPage?.pageInfo.endCursor,
    },
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    fetchMore: fetchNextPage,
  }
}
